/* eslint-disable max-lines */
import Swal from "sweetalert2";
import { set_token, set_user_details,getCookie, csrf_headers } from "@/router";
import { client_domain } from "@/main.js";
import { authFinaleSharedMethods } from "@/views/auth_finale_helper";

const REGISTRATION_REDIRECT_RULES = [
    {
        matches: (context) => context.is_organization_registration(),
        get_url: () => '/organisation/',
    },
    {
        matches: (context) => context.has_dynamic_forms_feature(),
        get_url: (context, user_details) => context.get_profile_name_update_url(user_details),
    },
    {
        matches: (context) => context.is_bosa_domain(),
        get_url: (context, user_details) => context.get_bosa_registration_redirect_url(user_details),
    },
    {
        matches: (context) => context.cbo_domain,
        get_url: () => '/view_events',
    },
    {
        matches: (context) => context.client_features?.marketplace,
        get_url: () => '/marketplace',
    },
];

export default {
    inject: ['client_features'],

    data() {
        return ({
            login_url: '',
            first_name: '',
            last_name: '',
            email: '',
            phone_number: '',
            account_type: 'individual',
            organization_name: '',
            type_of_organization: [],
            selected_type_of_organization: '',
            custom_type_of_organization: '',
            organization_access: null,
            user_type: 'parent',
            password: '',
            confirm_password: '',
            passwordVisible: false,
            confirmPasswordVisible: false,
            errors: {},
            is_registering: false,
            passed: false,
            cbo_domain: false,
            bosa_domain: false,
            vettedsa_domain: false,
            csrfToken: '',
            school_domain: false
        });
    },
    mounted() {
        if (client_domain.includes('cbo')) {
            this.cbo_domain = true;
        }
        if (this.is_bosa_domain()) {
            this.bosa_domain = true;
        }
        if (client_domain.includes('vettedsa')) {
            this.vettedsa_domain = true;
        }
        this.csrfToken = getCookie('csrftoken');
        if (client_domain.includes('chiefluthuli')) {
            this.school_domain = true;
        }
        if (this.is_organization_registration()) {
            this.fetch_type_of_organization();
        }
    },
    methods: {
        ...authFinaleSharedMethods,
        get_registration_response_json(jqXHR) {
            if (jqXHR?.responseJSON) return jqXHR.responseJSON;

            try {
                return JSON.parse(jqXHR?.responseText || '{}');
            } catch {
                return null;
            }
        },
        get_registration_error_text(error_value) {
            if (!error_value) return '';
            if (Array.isArray(error_value)) return error_value.join(' ');
            if (typeof error_value === 'object') {
                if (error_value.email) return this.get_registration_error_text(error_value.email);
                if (error_value.message) return this.get_registration_error_text(error_value.message);
                return Object.values(error_value)
                    .map((value) => this.get_registration_error_text(value))
                    .filter(Boolean)
                    .join(' ');
            }
            return String(error_value);
        },
        get_registration_error_message(response_status, httpStatus, textStatus) {
            const response_message = this.get_registration_error_text(
                response_status?.message
            );

            return response_message || this.get_registration_general_error(httpStatus, textStatus);
        },
        show_registration_error(message) {
            Swal.fire({
                icon: 'error',
                title: 'Registration failed',
                text: message,
                position: 'bottom-end',
                showConfirmButton: false,
                timer: 3000,
                toast: true
            });
        },
        apply_registration_field_error(response_status) {
            if (!response_status) return false;

            const field_errors = response_status.errors || response_status.data?.errors || {};
            const email_error = this.get_registration_error_text(
                field_errors.email ||
                response_status.message
            );
            if (email_error) {
                this.errors.email = email_error;
                return true;
            }

            if (field_errors.organization_name) {
                this.errors.organization_name = this.get_registration_error_text(field_errors.organization_name);
                return true;
            }

            const response_message = this.get_registration_error_text(
                response_status.message ||
                ''
            );
            const normalized_message = response_message.toLowerCase();
            if (normalized_message.includes('email')) {
                this.errors.email = response_message;
                return true;
            }

            if (normalized_message.includes('phone number')) {
                this.errors.phone_number = response_message;
                return true;
            }

            if (
                (normalized_message.includes('organization') || normalized_message.includes('organisation')) &&
                normalized_message.includes('exist')
            ) {
                this.errors.organization_name = response_message;
                return true;
            }

            return false;
        },
        get_registration_general_error(httpStatus, textStatus) {
            if (httpStatus === 403) {
                return 'Forbidden (403). CSRF token missing/invalid or cookies not included.';
            }

            if (httpStatus) {
                return `Request failed (HTTP ${httpStatus}).`;
            }

            return `Request failed (${textStatus || 'network error'}).`;
        },
        is_organization_registration() {
            return this.$route?.meta?.registrationType === 'organization';
        },

        get_registration_switch_route() {
            const route_name = this.is_organization_registration()
                ? 'register_personal'
                : 'register_organisation';

            return { // NOPMD -- PMD falsely treats a returned object literal as a block.
                name: route_name,
                query: this.$route?.query?.next ? { next: this.$route.query.next } : {},
            };
        },

        get_signup_endpoint() {
            return this.is_organization_registration() ? 'register_organization' : 'register_user';
        },

        async register_user() {
            if (this.is_registering) return;

            this.validateForm();

            if (!this.passed) {
                return;
            }

            this.clear_profile_modal_session();
            this.is_registering = true;
            try {
                await this.ensure_csrf_cookie();
            } catch {
                this.is_registering = false;
                this.errors.general = 'Could not prepare registration. Please try again.';
                this.show_registration_error(this.errors.general);
                return;
            }

            const baseUrl = `${window.location.origin}`;

            $.ajax({
                type: "POST",
                url: `${process.env.VUE_APP_BACKEND_API_URL}/system_management/${this.get_signup_endpoint()}/`,
                xhrFields: { withCredentials: true },
                headers: {
                    'Content-Type': 'application/json',
                    ...csrf_headers(this.csrfToken)
                },
                data: JSON.stringify({
                    role: "user",
                    email: this.email,
                    last_name: this.last_name,
                    first_name: this.first_name,
                    phone_number: this.phone_number,
                    account_type: this.is_organization_registration() ? 'organization' : this.account_type,
                    organization_name: this.is_organization_registration() ? this.organization_name : '',
                    type_of_organization_id: this.is_organization_registration() && !this.is_custom_organization_type() ? this.selected_type_of_organization : '',
                    type_of_organization: this.is_organization_registration() && this.is_custom_organization_type() ? this.custom_type_of_organization : '',
                    custom_type_of_organization: this.is_organization_registration() && this.is_custom_organization_type() ? this.custom_type_of_organization : '',
                    is_private: this.is_organization_registration() ? this.organization_access === 'private' : false,
                    user_type: this.user_type,
                    password: this.password,
                    base_url: baseUrl
                }),
                success: (response) => {
                    if (response.status === "success") {
                        const response_data = response.data;

                        if (!response_data?.token) {
                            window.location = '/login';
                            return;
                        }

                        const user_details = this.get_user_details_payload(response_data);
                        set_token(response_data.token);
                        set_user_details(user_details, { show_organisation: this.client_features?.show_organisation });
                        this.set_active_organization_profile(response_data);

                        const redirect_url = this.get_registration_redirect_url(user_details);
                        window.location = redirect_url;
                    }
                },
                error: (jqXHR, textStatus, errorThrown) => {
                    this.handle_registration_error(jqXHR, textStatus, errorThrown);
                },
                complete: () => {
                    this.is_registering = false;
                }
            });
        },
        handle_registration_error(jqXHR, textStatus, errorThrown) {
            const response_status = this.get_registration_response_json(jqXHR);
            const httpStatus = jqXHR?.status;
            const error_message = this.get_registration_error_message(response_status, httpStatus, textStatus);

            this.apply_registration_field_error(response_status);
            this.errors.general = error_message;
            this.show_registration_error(error_message);

            console.warn('register_user failed', {
                httpStatus,
                textStatus,
                errorThrown,
                response_status
            });
        },
        get_registration_redirect_url(user_details) {
            if (!this.is_organization_registration()) {
                const requested_url = this.get_requested_registration_redirect_url();
                if (requested_url) return requested_url;
            }
            const redirect_rule = REGISTRATION_REDIRECT_RULES.find((rule) =>
                rule.matches(this, user_details)
            );

            return redirect_rule
                ? redirect_rule.get_url(this, user_details)
                : this.get_default_registration_redirect_url();
        },
        get_bosa_registration_redirect_url(user_details) {
            return this.has_missing_required_profile_details(user_details)
                ? this.get_profile_name_update_url(user_details)
                : '/home';
        },
        get_default_registration_redirect_url() {
            return this.get_requested_registration_redirect_url() || '/home';
        },
        get_requested_registration_redirect_url() {
            const requested_url = this.$route?.query?.next || sessionStorage.getItem('next');
            if (requested_url) sessionStorage.removeItem('next');
            const normalized_url = String(requested_url || '').trim();
            return normalized_url.startsWith('/') && !normalized_url.startsWith('//')
                ? normalized_url
                : '';
        },
        get_profile_name_update_url(user_details) {
            this.set_profile_modal_session(user_details);
            return '/profile';
        },
        validateForm() {
            this.errors = {};
            const validation_results = [
                this.validate_organization_details(),
                this.validate_required_name_fields(),
                this.validate_email_field(),
                this.validate_password_field(),
                this.validate_confirm_password_field(),
            ];

            this.passed = validation_results.every(Boolean);
        },
        validate_organization_details() {
            if (!this.is_organization_registration()) return true;

            return [
                this.set_field_error(!this.organization_name, 'organization_name', 'Organisation name is required'),
                this.set_field_error(!this.selected_type_of_organization, 'type_of_organization_id', 'Type of organisation is required'),
                this.set_field_error(this.has_missing_custom_organization_type(), 'type_of_organization_id', 'Enter your type of organisation'),
                this.set_field_error(!this.organization_access, 'organization_access', 'Choose whether the organisation is public or private'),
            ].every(Boolean);
        },
        validate_required_name_fields() {
            return [
                this.set_field_error(this.has_short_name(this.first_name), 'first_name', 'First name is required'),
                this.set_field_error(this.has_short_name(this.last_name), 'last_name', 'Last name is required'),
            ].every(Boolean);
        },
        validate_email_field() {
            if (!this.email) return this.set_validation_error('email', 'Email is required');
            if (!this.isValidEmail(this.email)) return this.set_validation_error('email', 'Please enter a valid email');

            this.errors.email = '';
            return true;
        },
        validate_password_field() {
            if (!this.password) return this.set_validation_error('password', 'Password is required');
            if (!this.isValidPassword(this.password)) {
                return this.set_validation_error('password', 'Password must be at least 8 characters, contain at least one uppercase letter, one lowercase letter, one digit, and one special character');
            }

            this.errors.password = '';
            return true;
        },
        validate_confirm_password_field() {
            if (!this.confirm_password) return this.set_validation_error('confirm_password', 'Confirm password is required');
            if (this.password !== this.confirm_password) return this.set_validation_error('confirm_password', 'Passwords do not match');

            this.errors.confirm_password = '';
            return true;
        },
        set_field_error(has_error, field, message) {
            if (has_error) return this.set_validation_error(field, message);
            if (!Reflect.get(this.errors, field)) Reflect.set(this.errors, field, '');

            return true;
        },
        set_validation_error(field, message) {
            Reflect.set(this.errors, field, message);
            return false;
        },
        has_short_name(name) {
            return !name || name.length <= 2;
        },
        has_missing_custom_organization_type() {
            return this.is_custom_organization_type() && !this.custom_type_of_organization.trim();
        },
        fetch_type_of_organization() {
            $.ajax({
                url: `${process.env.VUE_APP_BACKEND_API_URL}/system_management/get_type_of_organization/`,
                type: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                success: (response) => {
                    if (response.status === 'success') {
                        this.type_of_organization = response.data.type_of_organization_data;
                    }
                },
            });
        },
        is_custom_organization_type() {
            return this.selected_type_of_organization === 'other';
        },
        isValidEmail(email) {
            const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return re.test(String(email).toLowerCase());
        },
        isValidPhoneNumber(phone_number) {
            const re = /^[\d\-+()]+$/;
            return re.test(phone_number);
        },

        togglePasswordVisibility(field) {
            if (field === 'password') {
                this.passwordVisible = !this.passwordVisible;
            } else if (field === 'confirm') {
                this.confirmPasswordVisible = !this.confirmPasswordVisible;
            }
        },
        isValidPassword(password) {
            const re = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
            return re.test(password);
        },


    }
};



